import React, { useEffect, useMemo, useRef, useState } from "react";
import useEscapeKey from "../../../hooks/useEscapeKey.tsx";
import axios from "axios";
import Swal from "sweetalert2";

type Lang = "no" | "en";
type PaymentMethod = "stripe" | "vipps";

type InvoiceLine = {
  id: number;
  article: string;
  description: string;
  price: string;
  quantity: string;
  fixed: boolean;
  recurring: boolean;
};

type DraftLine = Omit<InvoiceLine, "id">;

type CreateInvoiceModalProps = {
  lang: Lang;
  onLangChange: (lang: Lang) => void;
  onClose: () => void;
  onToast: (message: string) => void;
  baseUrl: string;
  client: any;
  currentUser: any;
};

type FieldProps = {
  label: string;
  value: string;
  setValue: (value: string) => void;
  placeholder?: string;
  type?: string;
};

type CustomerField = {
  key: string;
  labelNo: string;
  labelEn: string;
  type: "text" | "email" | "tel";
  placeholder: string;
  required?: boolean;
};
type ArticleField = {
  key: string;
  labelNo: string;
  labelEn: string;
  type: "text" | "email" | "tel" | "number" | "checkbox";
  placeholder: string;
  required?: boolean;
};

const customerRegistrationFieldsFromApi: CustomerField[] = [
  { key: "companyName", labelNo: "Firmanavn", labelEn: "Company name", type: "text", placeholder: "Nordic Elektro AS", required: true },
  { key: "orgNumber", labelNo: "Org.nr", labelEn: "Org.no", type: "text", placeholder: "999 999 999" },
  { key: "contactPerson", labelNo: "Kontaktperson", labelEn: "Contact person", type: "text", placeholder: "Ola Nordmann" },
  { key: "email", labelNo: "E-post", labelEn: "Email", type: "email", placeholder: "kunde@domene.no", required: true },
  { key: "phone", labelNo: "Telefon", labelEn: "Phone", type: "tel", placeholder: "+47 00 00 00 00" },
];
const articleRegistrationFieldsFromApi: ArticleField[] = [
  { key: "articleNumber", labelNo: "Artikkel nummer", labelEn: "Article number", type: "text", placeholder: "1000.01", required: true },
  { key: "description", labelNo: "Beskrivelse", labelEn: "Description", type: "text", placeholder: "Consultation with lawyer." },
  { key: "price", labelNo: "Pris", labelEn: "Price", type: "number", placeholder: "100", required: true },
  { key: "fixed", labelNo: "Bruk fastpris", labelEn: "Use fixed price", type: "checkbox", placeholder: "" },
  { key: "recurring", labelNo: "Månedlig", labelEn: "Monthly", type: "checkbox", placeholder: "" },
];

const text = {
  no: {
    title: "Opprett faktura",
    customer: "Kunde",
    registerCustomer: "Registrer kunde",
    email: "E-post",
    org: "Org.nr (valgfritt)",
    dueDays: "Forfallsdager",
    mainDescription: "Overordnet beskrivelse",
    description: "Beskrivelse",
    articleSearch: "Søk artikkel",
    addNewArticle: "Ny artikkel",
    price: "Pris (NOK)",
    draftLine: "Ny varelinje",
    invoicePreview: "Faktura preview",
    addToPreview: "Legg til i faktura preview",
    updatePreview: "Oppdater varelinje",
    clearLine: "Tøm linje",
    edit: "Rediger",
    removeLine: "Fjern",
    qty: "Antall / timer",
    vat: "MVA",
    fixed: "Bruk fastpris",
    monthly: "Månedlig",
    subtotal: "Delsum",
    vatLine: "MVA",
    total: "Totalt å betale",
    paymentMethods: "Betalingsmetoder",
    stripeInvoice: "Stripe Invoice",
    vippsBank: "Vipps til kundens nettbank",
    stripeHint: "Hosted payment page og invoice email",
    vippsHint: "Betalingskrav direkte via Vipps",
    create: "Opprett Faktura",
    draft: "Lagre utkast",
    cancel: "Avbryt",
    customerPlaceholder: "Søk kunde",
    emailPlaceholder: "kunde@domene.no",
    orgPlaceholder: "999 999 999",
    mainDescriptionPlaceholder: "Kort referanse eller intern kommentar",
    descriptionPlaceholder: "Arbeidsbeskrivelse / referanse",
    articlePlaceholder: "Søk etter artikkel, tjeneste eller varenummer",
    open: "Åpne fakturamodal",
    created: "Faktura opprettet",
    draftSaved: "Utkast lagret",
    registerTitle: "Registrer ny kunde",
    registerSubtitle: "Feltene hentes fra kunde-API og kan endres per marked/kundetype.",
    apiReady: "Kundefelter hentet fra API",
    saveCustomer: "Lagre kunde",
    saveArticle: "Lagre Artikkel",
    backToInvoice: "Tilbake til faktura",
    customerSaved: "Kunde registrert",
    lineHelp: "Fyll ut varelinjen og trykk Legg til. Først da kommer linjen inn i faktura preview og totalsummen.",
    emptyPreview: "Ingen varelinjer er lagt til ennå.",
    lineAdded: "Varelinje lagt til i faktura preview",
    lineUpdated: "Varelinje oppdatert",
  },
  en: {
    title: "Create invoice",
    customer: "Customer",
    registerCustomer: "Register customer",
    email: "Email",
    org: "Org.no (optional)",
    dueDays: "Due days",
    mainDescription: "Main description",
    description: "Description",
    articleSearch: "Search article",
    addNewArticle: "New article",
    price: "Price (NOK)",
    draftLine: "New invoice line",
    invoicePreview: "Invoice preview",
    addToPreview: "Add to invoice preview",
    updatePreview: "Update invoice line",
    clearLine: "Clear line",
    edit: "Edit",
    removeLine: "Remove",
    qty: "Quantity / hours",
    vat: "VAT",
    fixed: "Use fixed price",
    monthly: "Monthly",
    subtotal: "Subtotal",
    vatLine: "VAT",
    total: "Total to pay",
    paymentMethods: "Payment methods",
    stripeInvoice: "Stripe Invoice",
    vippsBank: "Vipps to customer bank",
    stripeHint: "Hosted payment page and invoice email",
    vippsHint: "Direct payment request through Vipps",
    create: "Create Invoice",
    draft: "Save draft",
    cancel: "Cancel",
    customerPlaceholder: "Search customer",
    emailPlaceholder: "customer@domain.com",
    orgPlaceholder: "999 999 999",
    mainDescriptionPlaceholder: "Short reference or internal note",
    descriptionPlaceholder: "Work description / reference",
    articlePlaceholder: "Search article, service or item number",
    open: "Open invoice modal",
    created: "Invoice created",
    draftSaved: "Draft saved",
    registerTitle: "Register new customer",
    registerSubtitle: "Fields are fetched from the customer API and can change by market/customer type.",
    apiReady: "Customer fields fetched from API",
    saveCustomer: "Save customer",
    saveArticle: "Save article",
    backToInvoice: "Back to invoice",
    customerSaved: "Customer registered",
    lineHelp: "Fill in the invoice line and click Add. Only then will it appear in the preview and totals.",
    emptyPreview: "No invoice lines have been added yet.",
    lineAdded: "Invoice line added to preview",
    lineUpdated: "Invoice line updated",
  },
};

const emptyDraftLine: DraftLine = {
  article: "",
  description: "",
  price: "1000",
  quantity: "1",
  fixed: false,
  recurring: false,
};

function parseNumber(value: string): number {
  return Number(value.replace(/[^0-9.-]/g, "")) || 0;
}

function getLineAmount(line: Pick<InvoiceLine, "price" | "quantity" | "fixed">) {
  const basePrice = parseNumber(line.price);
  const quantity = parseNumber(line.quantity);
  return line.fixed ? basePrice : basePrice * quantity;
}

function calculateTotals(lines: InvoiceLine[], vatPercent: string) {
  const subtotal = lines.reduce((sum, line) => sum + getLineAmount(line), 0);
  const vatRate = parseNumber(vatPercent) / 100;
  const vatAmount = Math.round(subtotal * vatRate);
  return { subtotal, vatAmount, total: subtotal + vatAmount };
}

function runCalculationTests() {
  const mixedLines: InvoiceLine[] = [
    { id: 1, article: "Consulting", description: "Consulting hours", price: "1000", quantity: "2", fixed: false, recurring: false },
    { id: 2, article: "Setup", description: "Platform setup", price: "5000", quantity: "99", fixed: true, recurring: false },
  ];

  const mixedTotals = calculateTotals(mixedLines, "25");
  console.assert(mixedTotals.subtotal === 7000, "Mixed subtotal should equal 7000");
  console.assert(mixedTotals.vatAmount === 1750, "Mixed VAT should equal 1750");
  console.assert(mixedTotals.total === 8750, "Mixed total should equal 8750");

  const zeroVatTotals = calculateTotals(mixedLines, "0");
  console.assert(zeroVatTotals.total === 7000, "Zero VAT total should equal subtotal");

  const emptyTotals = calculateTotals([], "25");
  console.assert(emptyTotals.total === 0, "Empty invoice should total 0");
}

runCalculationTests();

function formatCurrency(amount: number) {
  return `${new Intl.NumberFormat("nb-NO").format(amount)} kr`;
}

function Label({ children }: { children: React.ReactNode }) {
  return <label className="block text-[16px] font-bold text-[#414141]">{children}</label>;
}

function Field({ label, value, setValue, placeholder = "", type = "text" }: FieldProps) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        type={type}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder={placeholder}
        className="mt-2 h-[42px] w-full rounded-md border border-[#D8D3CC] bg-white px-4 text-[19px] text-[#1d1d1d] outline-none placeholder:text-[#777] focus:border-[#7B2FBE]"
      />
    </div>
  );
}

function PaymentMethodCard({ active, title, hint, onClick }: { active: boolean; title: string; hint: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-xl border px-4 py-3 text-left transition ${active ? "border-[#7B2FBE] bg-white shadow-[0_0_0_2px_rgba(123,47,190,0.12)]" : "border-[#D8D3CC] bg-[#FBFAF6] hover:bg-white"
        }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[15px] font-bold text-[#1A1A1A]">{title}</div>
          <div className="mt-1 text-xs text-[#6B6579]">{hint}</div>
        </div>
        <div className={`h-5 w-5 rounded-full border-2 ${active ? "border-[#7B2FBE] bg-[#7B2FBE]" : "border-[#BDB8B2] bg-white"}`} />
      </div>
    </button>
  );
}

export function CreateInvoiceModal({ lang, onLangChange, onClose, onToast, baseUrl, client, currentUser }: CreateInvoiceModalProps) {
  const t = text[lang];

  useEscapeKey(true, onClose);

  const invoiceInputs = JSON.parse(client.invoice_inputs) || {};

  const [customer, setCustomer] = useState("");
  const [customers, setCustomers] = useState<any[]>([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);

  const customerAbortRef = useRef<AbortController | null>(null);
  const customerSearchTimeout = useRef<NodeJS.Timeout | null>(null);
  const customerDropdownRef = useRef<HTMLDivElement | null>(null);

  // Article search states
  const [articles, setArticles] = useState<any[]>([]);
  const [articleLoading, setArticleLoading] = useState(false);
  const [showArticleDropdown, setShowArticleDropdown] = useState(false);

  const articleAbortRef = useRef<AbortController | null>(null);
  const articleSearchTimeout = useRef<NodeJS.Timeout | null>(null);
  const articleDropdownRef = useRef<HTMLDivElement | null>(null);


  const [email, setEmail] = useState("");
  const [org, setOrg] = useState("");
  const [dueDays, setDueDays] = useState(invoiceInputs.due_date_days ? String(invoiceInputs.due_date_days) : "14");
  const [mainDescription, setMainDescription] = useState("");
  const [vat, setVat] = useState("25");
  const [draftLine, setDraftLine] = useState<DraftLine>(emptyDraftLine);
  const [editingLineId, setEditingLineId] = useState<number | null>(null);
  const [invoiceLines, setInvoiceLines] = useState<InvoiceLine[]>([]);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("stripe");
  const [registeringCustomer, setRegisteringCustomer] = useState(false);
  const [customerFields, setCustomerFields] = useState<CustomerField[]>([]);
  const [customerDraft, setCustomerDraft] = useState<Record<string, string>>({});

  const totals = useMemo(() => calculateTotals(invoiceLines, vat), [invoiceLines, vat]);
  const [loading, setLoading] = useState(false);


  // search customers event handlers and effect
  const searchCustomers = async (query: string) => {
    if (!baseUrl || !client || !currentUser) return;

    // Minimum chars before searching
    if (query.trim().length < 2) {
      setCustomers([]);
      return;
    }

    try {
      // Abort previous request
      if (customerAbortRef.current) {
        customerAbortRef.current.abort();
      }

      const controller = new AbortController();
      customerAbortRef.current = controller;

      setCustomerLoading(true);

      const response = await axios.get(
        `${baseUrl}/getCustomerByNameOrEmail`,
        {
          params: {
            token: currentUser.token,
            client_uid: client.uid,
            query: query.trim(),
          },
          headers: {
            "Content-Type": "application/json",
          },
          signal: controller.signal,
        }
      );



      if (!controller.signal.aborted) {
        if (response.data.status === "OK") {
          setCustomers(response?.data.data || []);
        }
      }
    } catch (err: any) {
      if (err.name !== "AbortError") {
        console.error(err);
      }
    } finally {
      setCustomerLoading(false);
    }
  };

  const selectCustomer = (customerData: any) => {
    setSelectedCustomer(customerData);

    setCustomer(customerData.company_name || customerData.name || "");
    setEmail(customerData.email || "");
    setOrg(customerData.org_no || "");

    setShowCustomerDropdown(false);
    setCustomers([]);
  };

  useEffect(() => {
    if (!customer.trim()) {
      setCustomers([]);
      return;
    }

    if (customerSearchTimeout.current) {
      clearTimeout(customerSearchTimeout.current);
    }

    customerSearchTimeout.current = setTimeout(() => {
      searchCustomers(customer);
    }, 400);

    return () => {
      if (customerSearchTimeout.current) {
        clearTimeout(customerSearchTimeout.current);
      }
    };
  }, [customer]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {

      if (
        customerDropdownRef.current &&
        !customerDropdownRef.current.contains(event.target as Node)
      ) {
        setShowCustomerDropdown(false);
      }

      if (
        articleDropdownRef.current &&
        !articleDropdownRef.current.contains(event.target as Node)
      ) {
        setShowArticleDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // search articles event handlers and effect
  const searchArticles = async (query: string) => {
    if (!baseUrl || !client || !currentUser) return;

    if (query.trim().length < 2) {
      setArticles([]);
      return;
    }

    try {
      if (articleAbortRef.current) {
        articleAbortRef.current.abort();
      }

      const controller = new AbortController();
      articleAbortRef.current = controller;

      setArticleLoading(true);

      const response = await axios.get(
        `${baseUrl}/getCustomerArticles`,
        {
          params: {
            token: currentUser.token,
            client_uid: client.uid,
            query: query.trim(),
          },
          signal: controller.signal,
        }
      );

      if (!controller.signal.aborted) {
        if (response.data.status === "OK") {
          setArticles(response.data.data || []);
        }
      }
    } catch (err: any) {
      if (err.name !== "AbortError") {
        console.error(err);
      }
    } finally {
      setArticleLoading(false);
    }
  };

  useEffect(() => {
    if (!draftLine.article.trim()) {
      setArticles([]);
      return;
    }

    if (articleSearchTimeout.current) {
      clearTimeout(articleSearchTimeout.current);
    }

    articleSearchTimeout.current = setTimeout(() => {
      searchArticles(draftLine.article);
    }, 400);

    return () => {
      if (articleSearchTimeout.current) {
        clearTimeout(articleSearchTimeout.current);
      }
    };
  }, [draftLine.article]);

  const selectArticle = (article: any) => {
    setDraftLine((current) => ({
      ...current,
      article: article.article_number || "",
      description: article.description || "",
      price: article.price ? String(article.price) : "0",
      fixed: article.fixed_price ? true : false,
    }));

    setShowArticleDropdown(false);
    setArticles([]);
  };

  const updateDraftLine = (key: keyof DraftLine, value: string | boolean) => {
    setDraftLine((current) => ({ ...current, [key]: value }));
  };

  const clearDraftLine = () => {
    setDraftLine(emptyDraftLine);
    setEditingLineId(null);
  };

  const addOrUpdateLine = () => {
    const normalizedLine: DraftLine = {
      article: draftLine.article.trim(),
      description: draftLine.description.trim(),
      price: draftLine.price || "0",
      quantity: draftLine.fixed ? "1" : draftLine.quantity || "1",
      fixed: draftLine.fixed,
      recurring: draftLine.recurring,
    };

    if (editingLineId) {
      setInvoiceLines((current) => current.map((line) => (line.id === editingLineId ? { ...normalizedLine, id: editingLineId } : line)));
      onToast(t.lineUpdated);
    } else {
      setInvoiceLines((current) => [...current, { ...normalizedLine, id: Date.now() }]);
      onToast(t.lineAdded);
    }

    clearDraftLine();
  };

  const editLine = (line: InvoiceLine) => {
    setDraftLine({ article: line.article, description: line.description, price: line.price, quantity: line.quantity, fixed: line.fixed, recurring: line.recurring });
    setEditingLineId(line.id);
  };

  const removeInvoiceLine = (id: number) => {
    setInvoiceLines((current) => current.filter((line) => line.id !== id));
    if (editingLineId === id) {
      clearDraftLine();
    }
  };

  const openCustomerRegistration = () => {
    setRegisteringCustomer(true);
    setCustomerFields(customerRegistrationFieldsFromApi);
    setCustomerDraft({});
    onToast(t.apiReady);
  };
  const [articleFields, setArticleFields] = useState<any[]>([]);
  type ArticleDraft = {
    articleNumber: string;
    description: string;
    price: string;
    recurring: boolean;
    fixed: boolean;
  };
  const [articleDraft, setArticleDraft] = useState<ArticleDraft>({
    articleNumber: "",
    description: "",
    price: "",
    recurring: false,
    fixed: false,
  });
  const [registeringArticle, setRegisteringArticle] = useState(false);
  const openArticleRegistration = () => {
    axios.get(baseUrl + "/getCustomerArticleNumber", { params: { token: currentUser.token, client_uid: client.uid } }).then((response) => {
      if (response.data.status === "OK") {
        setRegisteringArticle(true);
        setArticleFields(articleRegistrationFieldsFromApi);
        setArticleDraft((current) => ({
          ...current,
          articleNumber: response.data.data.articleNumber,
        }));
        onToast(t.apiReady);
      }
    });
  };

  const updateCustomerDraft = (key: string, value: string) => {
    setCustomerDraft((current) => ({ ...current, [key]: value }));
  };
  const updateArticleDraft = (
    key: keyof ArticleDraft,
    value: string | boolean
  ) => {
    setArticleDraft((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const saveRegisteredCustomer = async () => {
    const newCustomer = customerDraft.companyName || customerDraft.contactPerson || "Ny kunde";
    try {
      let formData = new FormData();
      formData.append("token", currentUser.token);
      formData.append("client_uid", client.uid);
      formData.append("companyName", customerDraft.companyName || "");
      formData.append("orgNumber", customerDraft.orgNumber || "");
      formData.append("contactPerson", customerDraft.contactPerson || "");
      formData.append("email", customerDraft.email || "");
      formData.append("phone", customerDraft.phone || "");
      const response = await axios.post(baseUrl + "/createCustomer", formData);
      if (response.data.status === "OK") {
        setCustomer(newCustomer);
        setSelectedCustomer(response.data.data);
        setEmail(customerDraft.email || "");
        setOrg(customerDraft.orgNumber || "");
        setRegisteringCustomer(false);
        onToast(t.customerSaved);
      }
    } catch (err) {
      console.error("Error creating customer:", err);
    }
  };
  const saveRegisteredArticle = async () => {
    // const newArticle = articleDraft.articleNumber || articleDraft.description || "Ny kunde";
    // selectArticle(newArticle);
    let formData = new FormData();
    formData.append("article_number", articleDraft.articleNumber || "");
    formData.append("description", articleDraft.description || "");
    formData.append("price", articleDraft.price || "0");
    formData.append("fixed", articleDraft.fixed ? "1" : "0");
    formData.append("recurring", articleDraft.recurring ? "1" : "0");
    formData.append("token", currentUser.token);
    formData.append("client_uid", client.uid);
    const response = await axios.post(baseUrl + "/createCustomerArticle", formData);
    if (response.data.status === "OK") {
      updateDraftLine("article", articleDraft.articleNumber || "");
      updateDraftLine("description", articleDraft.description || "");
      updateDraftLine("price", articleDraft.price || "");
      updateDraftLine("fixed", articleDraft.fixed ? "1" : "0");
      updateDraftLine("recurring", articleDraft.recurring ? "1" : "0");
      setRegisteringArticle(false);
    }
    // addOrUpdateLine();
    // onToast(t.customerSaved);
  };

  const handleActionInvoice = async (type: string) => {
    let formData = new FormData();
    formData.append("token", currentUser.token);
    formData.append("client_uid", client.uid);
    formData.append("customer", JSON.stringify(selectedCustomer));
    formData.append("org_no", org);
    formData.append("invoiceItems", JSON.stringify(invoiceLines));
    formData.append("mainDescription", mainDescription);
    formData.append("vat", vat);
    formData.append("dueDays", dueDays);
    formData.append("paymentMethod", paymentMethod);
    formData.append("type", type);

    try {
      setLoading(true);
      const response = await axios.post(baseUrl + "/createCustomerInvoice", formData);
      if (response.data.status === "OK") {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response.data.data || "An error occurred while creating the invoice.",
        });
        onToast(t.created);
        onClose();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.data.data || "An error occurred while creating the invoice.",
        });
      }
    } catch (err) {
      console.error("Error creating invoice:", err);
    } finally {
      setLoading(false);
    }

  }

  const draftLineAmount = getLineAmount(draftLine);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-2 sm:p-4" onClick={onClose}>
      <div
        onClick={(event) => event.stopPropagation()}
        className="relative mx-auto my-2 flex max-h-[calc(100vh-16px)] w-full max-w-[760px] flex-col overflow-hidden rounded-2xl border border-[#D8D3CC] bg-[#FBFAF6] shadow-[0_24px_60px_rgba(0,0,0,0.22)] sm:my-4 sm:max-h-[calc(100vh-32px)]"
      >
        {loading && (
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 border-4 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-3 text-gray-600 animate-pulse">Please wait...</p>
            </div>
          </div>
        )}
        {!loading && (
          <>
            <div className="flex shrink-0 items-center justify-between border-b border-[#D8D3CC] px-7 py-6">
              <h1 className="text-[22px] font-bold tracking-[-0.02em] text-[#161616]">{t.title}</h1>
              <div className="flex items-center gap-3">
                <div className="flex rounded-full border border-[#C9C4C0] bg-[#F2F0EB] p-1">
                  <button type="button" onClick={() => onLangChange("no")} className={`rounded-full px-4 py-1.5 text-sm font-bold ${lang === "no" ? "bg-[#7B2FBE] text-white shadow" : "text-[#3f3f3f]"}`}>
                    NO
                  </button>
                  <button type="button" onClick={() => onLangChange("en")} className={`rounded-full px-4 py-1.5 text-sm font-bold ${lang === "en" ? "bg-[#7B2FBE] text-white shadow" : "text-[#3f3f3f]"}`}>
                    EN
                  </button>
                </div>
                <button type="button" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-full border border-[#BDB8B2] text-2xl text-[#333] hover:bg-[#F0EEE8]">
                  x
                </button>
              </div>
            </div>

            <div className="flex-1 space-y-5 overflow-y-auto px-7 py-7">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <Label>{t.customer}</Label>
                    <button type="button" onClick={openCustomerRegistration} className="text-[13px] font-semibold text-[#7B2FBE] transition hover:text-[#5E1F96]">
                      + {t.registerCustomer}
                    </button>
                  </div>
                  <div ref={customerDropdownRef} className="relative">
                    <input
                      value={customer}
                      onChange={(event) => {
                        setCustomer(event.target.value);
                        setShowCustomerDropdown(true);
                        setSelectedCustomer(null);
                      }}
                      onFocus={() => {
                        if (customers.length) {
                          setShowCustomerDropdown(true);
                        }
                      }}
                      placeholder={t.customerPlaceholder}
                      className="h-[42px] w-full rounded-md border border-[#D8D3CC] bg-white px-4 text-[19px] text-[#1d1d1d] outline-none placeholder:text-[#777] focus:border-[#7B2FBE]"
                    />

                    {customerLoading ? (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#777]">
                        Searching...
                      </div>
                    ) : null}

                    {showCustomerDropdown && customers.length > 0 ? (
                      <div className="absolute z-20 mt-2 max-h-[260px] w-full overflow-y-auto rounded-xl border border-[#D8D3CC] bg-white shadow-lg">
                        {customers.map((customerItem: any) => (
                          <button
                            key={customerItem.id}
                            type="button"
                            onClick={() => selectCustomer(customerItem)}
                            className="w-full border-b border-[#F1F1F1] px-4 py-3 text-left hover:bg-[#F8F6FC] last:border-b-0"
                          >
                            <div className="font-semibold text-[#1A1A1A]">
                              {customerItem.companyName || customerItem.name}
                            </div>

                            <div className="mt-1 text-sm text-[#6B6579]">
                              {customerItem.email}
                            </div>

                            {customerItem.orgNumber ? (
                              <div className="mt-1 text-xs text-[#9A948E]">
                                Org: {customerItem.orgNumber}
                              </div>
                            ) : null}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
                <Field label={t.email} value={email} setValue={setEmail} placeholder={t.emailPlaceholder} type="email" />
                <Field label={t.org} value={org} setValue={setOrg} placeholder={t.orgPlaceholder} />
                <Field label={t.dueDays} value={dueDays} setValue={setDueDays} placeholder="14" />
              </div>

              <div>
                <Label>{t.mainDescription}</Label>
                <textarea
                  value={mainDescription}
                  onChange={(event) => setMainDescription(event.target.value)}
                  placeholder={t.mainDescriptionPlaceholder}
                  className="mt-2 h-[76px] w-full resize-y rounded-md border border-[#D8D3CC] bg-white px-4 py-3 text-[18px] text-[#1d1d1d] outline-none placeholder:text-[#777] focus:border-[#7B2FBE]"
                />
              </div>

              <div className="rounded-xl border border-[#D8D3CC] bg-white p-5">
                <div className="mb-5">
                  <div className="text-[18px] font-bold text-[#1A1A1A]">{t.draftLine}</div>
                  <div className="mt-1 text-sm text-[#6B6579]">{t.lineHelp}</div>
                </div>

                <div className="space-y-4 rounded-xl border border-[#E5E0DA] bg-[#FCFCFA] p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-bold uppercase tracking-[0.08em] text-[#7B2FBE]">
                      {editingLineId ? t.updatePreview : t.addToPreview}
                    </div>
                    <button
                      type="button"
                      onClick={() => updateDraftLine("fixed", !draftLine.fixed)}
                      className={`flex items-center gap-3 rounded-xl border px-4 py-2 transition ${draftLine.fixed ? "border-[#7B2FBE] bg-[#F6EFFB]" : "border-[#C9C4C0] bg-white"}`}
                    >
                      <div className={`h-5 w-5 rounded-full border-2 ${draftLine.fixed ? "border-[#7B2FBE] bg-[#7B2FBE]" : "border-[#BDB8B2] bg-white"}`} />
                      <span className="text-[14px] font-semibold text-[#1f1f1f]">{t.fixed}</span>
                    </button>
                  </div>

                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <Label>{t.articleSearch}</Label>
                      <button type="button" onClick={openArticleRegistration} className="text-[13px] font-semibold text-[#7B2FBE] transition hover:text-[#5E1F96]">
                        + {t.addNewArticle}
                      </button>
                    </div>
                    <div className="relative mt-2">
                      <div ref={articleDropdownRef} className="relative">
                        <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#7B2FBE]">
                          <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.35-4.35" />
                          </svg>
                        </span>
                        <input
                          value={draftLine.article}
                          onChange={(event) => {
                            updateDraftLine("article", event.target.value);
                            setShowArticleDropdown(true);
                          }}
                          onFocus={() => {
                            if (articles.length) {
                              setShowArticleDropdown(true);
                            }
                          }}
                          placeholder={t.articlePlaceholder}
                          className="w-full rounded-md border border-[#D8D3CC] bg-white py-3 pl-12 pr-4 text-[18px] text-[#1d1d1d] outline-none placeholder:text-[#777] focus:border-[#7B2FBE]"
                        />

                        {articleLoading ? (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#777]">
                            Searching...
                          </div>
                        ) : null}

                        {showArticleDropdown && articles.length > 0 ? (
                          <div className="absolute z-20 mt-2 max-h-[260px] w-full overflow-y-auto rounded-xl border border-[#D8D3CC] bg-white shadow-lg">
                            {articles.map((article: any) => (
                              <button
                                key={article.id}
                                type="button"
                                onClick={() => selectArticle(article)}
                                className="w-full border-b border-[#F1F1F1] px-4 py-3 text-left hover:bg-[#F8F6FC] last:border-b-0"
                              >
                                <div className="font-semibold text-[#1A1A1A]">
                                  {article.article_number} {article.name}
                                </div>

                                {article.description ? (
                                  <div className="mt-1 text-sm text-[#6B6579]">
                                    {article.description}
                                  </div>
                                ) : null}

                                <div className="mt-1 text-xs text-[#9A948E]">
                                  {article.price} NOK
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div>
                    {/* <Label>{t.description}</Label> */}
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-bold">
                        {t.description}
                      </div>
                      <button
                        type="button"
                        onClick={() => updateDraftLine("recurring", !draftLine.recurring)}
                        className={`flex items-center gap-3 rounded-xl border px-4 py-2 transition ${draftLine.recurring ? "border-[#7B2FBE] bg-[#F6EFFB]" : "border-[#C9C4C0] bg-white"}`}
                      >
                        <div className={`h-5 w-5 rounded-full border-2 ${draftLine.recurring ? "border-[#7B2FBE] bg-[#7B2FBE]" : "border-[#BDB8B2] bg-white"}`} />
                        <span className="text-[14px] font-semibold text-[#1f1f1f]">{t.monthly}</span>
                      </button>
                    </div>
                    <textarea
                      value={draftLine.description}
                      onChange={(event) => updateDraftLine("description", event.target.value)}
                      placeholder={t.descriptionPlaceholder}
                      className="mt-2 h-[72px] w-full resize-none rounded-md border border-[#D8D3CC] bg-white px-4 py-3 text-[17px] text-[#1d1d1d] outline-none placeholder:text-[#777] focus:border-[#7B2FBE]"
                    />
                  </div>

                  <div className={`grid gap-4 ${draftLine.fixed ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"}`}>
                    <Field label={t.price} value={draftLine.price} setValue={(value) => updateDraftLine("price", value)} />
                    {!draftLine.fixed ? <Field label={t.qty} value={draftLine.quantity} setValue={(value) => updateDraftLine("quantity", value)} /> : null}
                  </div>

                  <div className="flex flex-col gap-3 rounded-xl border border-[#E9E3DB] bg-[#F8F6F2] px-4 py-4 md:flex-row md:items-center md:justify-between">
                    <div>
                      <div className="text-sm font-semibold text-[#1A1A1A]">{lang === "no" ? "Linjesum" : "Line amount"}</div>
                      <div className="mt-1 text-xl font-bold text-[#7B2FBE]">{formatCurrency(draftLineAmount)}</div>
                    </div>
                    <div className="flex gap-2">
                      <button type="button" onClick={clearDraftLine} className="rounded-xl border border-[#C9C4C0] bg-white px-4 py-3 text-sm font-semibold text-[#333] hover:bg-[#F7F7F7]">
                        {t.clearLine}
                      </button>
                      <button type="button" onClick={addOrUpdateLine} className="rounded-xl bg-[#7B2FBE] px-5 py-3 text-sm font-bold text-white transition hover:bg-[#5E1F96]">
                        {editingLineId ? t.updatePreview : t.addToPreview}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-[#D8D3CC] bg-white p-5">
                <div className="mb-4 flex items-center justify-between gap-4">
                  <div>
                    <div className="text-[18px] font-bold text-[#1A1A1A]">{t.invoicePreview}</div>
                    <div className="mt-1 text-sm text-[#6B6579]">{invoiceLines.length ? `${invoiceLines.length} ${lang === "no" ? "varelinje(r)" : "line(s)"}` : t.emptyPreview}</div>
                  </div>
                </div>

                {invoiceLines.length ? (
                  <div className="overflow-hidden rounded-xl border border-[#E5E0DA]">
                    {invoiceLines.map((line, index) => (
                      <div key={line.id} className="grid grid-cols-[1fr_auto] gap-4 border-b border-[#E5E0DA] bg-[#FCFCFA] px-4 py-4 last:border-b-0">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="rounded-full bg-[#F6EFFB] px-2 py-1 text-xs font-bold text-[#7B2FBE]">#{index + 1}</span>
                            <strong className="text-[15px] text-[#1A1A1A]">{line.article || (lang === "no" ? "Uten artikkelnavn" : "No article name")}</strong>
                          </div>
                          {line.description ? <div className="mt-2 text-sm text-[#6B6579]">{line.description}</div> : null}
                          <div className="mt-2 text-xs text-[#6B6579]">
                            {line.fixed ? t.fixed : `${formatCurrency(parseNumber(line.price))} x ${line.quantity}`}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-[17px] font-bold text-[#1A1A1A]">{formatCurrency(getLineAmount(line))}</div>
                          <div className="mt-2 flex justify-end gap-2">
                            <button type="button" onClick={() => editLine(line)} className="rounded-lg border border-[#D8B4FE] bg-[#F7EFFD] px-3 py-2 text-xs font-semibold text-[#7B2FBE] hover:bg-[#EFE3FA]">
                              {t.edit}
                            </button>
                            <button type="button" onClick={() => removeInvoiceLine(line.id)} className="rounded-lg border border-[#FECACA] bg-white px-3 py-2 text-xs font-semibold text-[#EF4444] hover:bg-[#FFF1F1]">
                              {t.removeLine}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-[#D8D3CC] bg-[#FCFCFA] px-5 py-8 text-center text-sm text-[#6B6579]">{t.emptyPreview}</div>
                )}
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-[172px_1fr]">
                <div>
                  <Label>{t.vat}</Label>
                  <select value={vat} onChange={(event) => setVat(event.target.value)} className="mt-2 h-[42px] w-full rounded-md border border-[#D8D3CC] bg-white px-4 text-[17px] outline-none focus:border-[#7B2FBE]">
                    <option value="25">25 %</option>
                    <option value="15">15 %</option>
                    <option value="12">12 %</option>
                    <option value="0">0 %</option>
                  </select>
                </div>
              </div>

              <div className="overflow-hidden rounded-xl border border-[#CFC9C1] bg-white">
                <div className="space-y-2 px-5 py-4 text-[17px]">
                  <div className="flex justify-between"><span>{t.subtotal}</span><strong>{formatCurrency(totals.subtotal)}</strong></div>
                  <div className="flex justify-between"><span>{`${t.vatLine} (${vat}%)`}</span><strong>{formatCurrency(totals.vatAmount)}</strong></div>
                </div>
                <div className="flex items-center justify-between border-t border-[#CFC9C1] bg-[#F6EFFB] px-5 py-4">
                  <strong className="text-[18px]">{t.total}</strong>
                  <strong className="text-[24px] text-[#7B2FBE]">{formatCurrency(totals.total)}</strong>
                </div>
              </div>

              <div className="rounded-lg border border-[#D8B4FE] bg-[#F7EFFD] px-5 py-4">
                <div className="mb-3 text-[16px] font-semibold text-[#7B2FBE]">{t.paymentMethods}</div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <PaymentMethodCard active={paymentMethod === "stripe"} title={t.stripeInvoice} hint={t.stripeHint} onClick={() => setPaymentMethod("stripe")} />
                  <PaymentMethodCard active={paymentMethod === "vipps"} title={t.vippsBank} hint={t.vippsHint} onClick={() => setPaymentMethod("vipps")} />
                </div>
              </div>
            </div>

            {registeringCustomer ? (
              <div className="absolute inset-0 z-10 flex flex-col bg-[#FBFAF6]">
                <div className="shrink-0 border-b border-[#D8D3CC] px-7 py-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="text-[22px] font-bold tracking-[-0.02em] text-[#161616]">{t.registerTitle}</h2>
                      <p className="mt-1 max-w-[520px] text-sm text-[#6B6579]">{t.registerSubtitle}</p>
                    </div>
                    <button type="button" onClick={() => setRegisteringCustomer(false)} className="rounded-lg border border-[#C9C4C0] bg-white px-4 py-2 text-sm font-semibold text-[#333] hover:bg-[#F7F7F7]">
                      {t.backToInvoice}
                    </button>
                  </div>
                </div>

                <div className="flex-1 space-y-4 overflow-y-auto px-7 py-7">
                  <div className="rounded-xl border border-[#D8B4FE] bg-[#F7EFFD] px-5 py-4 text-sm text-[#7B2FBE]">API: /customers/registration-schema - {customerFields.length} fields</div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {customerFields.map((field) => (
                      <div key={field.key}>
                        <Label>{lang === "no" ? field.labelNo : field.labelEn}{field.required ? " *" : ""}</Label>
                        <input
                          type={field.type}
                          value={customerDraft[field.key] || ""}
                          onChange={(event) => updateCustomerDraft(field.key, event.target.value)}
                          placeholder={field.placeholder}
                          className="mt-2 h-[42px] w-full rounded-md border border-[#D8D3CC] bg-white px-4 text-[19px] text-[#1d1d1d] outline-none placeholder:text-[#777] focus:border-[#7B2FBE]"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="shrink-0 border-t border-[#D8D3CC] px-7 py-5">
                  <button type="button" onClick={saveRegisteredCustomer} className="w-full rounded-xl bg-[#7B2FBE] py-4 text-[19px] font-bold text-white transition hover:bg-[#5E1F96]">
                    {t.saveCustomer}
                  </button>
                </div>
              </div>
            ) : null}
            {registeringArticle ? (
              <div className="absolute inset-0 z-10 flex flex-col bg-[#FBFAF6]">
                <div className="shrink-0 border-b border-[#D8D3CC] px-7 py-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="text-[22px] font-bold tracking-[-0.02em] text-[#161616]">{t.registerTitle}</h2>
                      <p className="mt-1 max-w-[520px] text-sm text-[#6B6579]">{t.registerSubtitle}</p>
                    </div>
                    <button type="button" onClick={() => setRegisteringArticle(false)} className="rounded-lg border border-[#C9C4C0] bg-white px-4 py-2 text-sm font-semibold text-[#333] hover:bg-[#F7F7F7]">
                      {t.backToInvoice}
                    </button>
                  </div>
                </div>

                <div className="flex-1 space-y-4 overflow-y-auto px-7 py-7">
                  <div className="rounded-xl border border-[#D8B4FE] bg-[#F7EFFD] px-5 py-4 text-sm text-[#7B2FBE]">API: /customers/registration-schema - {customerFields.length} fields</div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {articleFields
                      .filter((field) => field.type !== "checkbox")
                      .map((field) => (
                        <div key={field.key}>
                          <Label>
                            {lang === "no" ? field.labelNo : field.labelEn}
                            {field.required ? " *" : ""}
                          </Label>

                          <input
                            type={field.type}
                            value={String(articleDraft[field.key as keyof ArticleDraft] || "")}
                            onChange={(event) =>
                              updateArticleDraft(
                                field.key as keyof ArticleDraft,
                                event.target.value
                              )
                            }
                            placeholder={field.placeholder}
                            className="mt-2 h-[42px] w-full rounded-md border border-[#D8D3CC] bg-white px-4 text-[19px] text-[#1d1d1d] outline-none placeholder:text-[#777] focus:border-[#7B2FBE]"
                          />
                        </div>
                      ))}

                    <div className="flex flex-wrap gap-3 mt-4">

                      <button
                        type="button"
                        onClick={() =>
                          updateArticleDraft("fixed", !articleDraft.fixed)
                        }
                        className={`flex items-center gap-3 rounded-xl border px-4 py-2 transition ${articleDraft.fixed
                          ? "border-[#7B2FBE] bg-[#F6EFFB]"
                          : "border-[#C9C4C0] bg-white"
                          }`}
                      >
                        <div
                          className={`h-5 w-5 rounded-full border-2 ${articleDraft.fixed
                            ? "border-[#7B2FBE] bg-[#7B2FBE]"
                            : "border-[#BDB8B2] bg-white"
                            }`}
                        />

                        <span className="text-[14px] font-semibold text-[#1f1f1f]">
                          {lang === "no" ? "Bruk fastpris" : "Use fixed price"}
                        </span>
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          updateArticleDraft("recurring", !articleDraft.recurring)
                        }
                        className={`flex items-center gap-3 rounded-xl border px-4 py-2 transition ${articleDraft.recurring
                          ? "border-[#7B2FBE] bg-[#F6EFFB]"
                          : "border-[#C9C4C0] bg-white"
                          }`}
                      >
                        <div
                          className={`h-5 w-5 rounded-full border-2 ${articleDraft.recurring
                            ? "border-[#7B2FBE] bg-[#7B2FBE]"
                            : "border-[#BDB8B2] bg-white"
                            }`}
                        />

                        <span className="text-[14px] font-semibold text-[#1f1f1f]">
                          {lang === "no" ? "Månedlig" : "Monthly"}
                        </span>
                      </button>

                    </div>
                  </div>
                </div>

                <div className="shrink-0 border-t border-[#D8D3CC] px-7 py-5">
                  <button type="button" onClick={saveRegisteredArticle} className="w-full rounded-xl bg-[#7B2FBE] py-4 text-[19px] font-bold text-white transition hover:bg-[#5E1F96]">
                    {t.saveArticle}
                  </button>
                </div>
              </div>
            ) : null}

            <div className="shrink-0 border-t border-[#D8D3CC] px-7 py-5">
              <button type="button" onClick={(e) => handleActionInvoice('create')} className="mb-3 w-full rounded-xl bg-[#7B2FBE] py-4 text-[19px] font-bold text-white transition hover:bg-[#5E1F96]">
                {t.create}
              </button>
              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={(e) => handleActionInvoice('draft')} className="rounded-lg border border-[#C9C4C0] bg-white py-3 text-[16px] font-medium text-[#333] hover:bg-[#F7F7F7]">{t.draft}</button>
                <button type="button" onClick={onClose} className="rounded-lg border border-[#FFB6B6] bg-white py-3 text-[16px] font-medium text-[#EF4444] hover:bg-[#FFF1F1]">{t.cancel}</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
